import React, { useEffect, useMemo, useState } from 'react';
import { Mail, Trash2, Download, Search } from 'lucide-react';
import { newsletterApi } from '../../lib/api';
import { Input } from '../../components/ui/input';

export default function AdminNewsletter() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState('');

  const refresh = () => {
    newsletterApi.list().then(setItems).catch(() => setItems([])).finally(() => setLoading(false));
  };
  useEffect(refresh, []);

  const filtered = useMemo(
    () => items.filter((i) => (i.email + ' ' + (i.name || '')).toLowerCase().includes(q.toLowerCase())),
    [items, q],
  );

  const remove = async (id) => {
    if (!window.confirm('Remove this subscriber?')) return;
    await newsletterApi.remove(id);
    refresh();
  };

  const exportCsv = () => {
    const rows = [['email', 'name', 'subscribed_at', 'status']];
    for (const i of items) rows.push([i.email, i.name || '', i.subscribed_at || '', i.status || '']);
    const csv = rows.map((r) => r.map((c) => `"${String(c).replace(/"/g, '""')}"`).join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = window.document.createElement('a');
    a.href = url; a.download = 'subscribers.csv'; a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div>
      <div className="mb-5 flex items-center justify-between flex-wrap gap-3">
        <div>
          <h2 className="text-xl font-bold text-[#0b1f3a]">Newsletter subscribers</h2>
          <p className="text-slate-500 text-sm mt-1">{items.length} subscribers.</p>
        </div>
        <div className="flex items-center gap-2">
          <div className="relative w-64">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" size={14} />
            <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search email or name" className="pl-9 h-10" />
          </div>
          <button onClick={exportCsv} className="btn-outline"><Download size={14} /> Export CSV</button>
        </div>
      </div>

      <div className="bg-white border border-slate-200 rounded-2xl overflow-hidden">
        {loading ? (
          <p className="p-8 text-center text-slate-500 text-sm">Loading...</p>
        ) : filtered.length === 0 ? (
          <div className="p-10 text-center">
            <Mail size={28} className="mx-auto text-slate-300" />
            <p className="mt-3 text-slate-700 font-semibold">No subscribers yet</p>
            <p className="text-slate-500 text-sm mt-1">Add a newsletter form to your footer to start collecting emails.</p>
          </div>
        ) : (
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-slate-500 text-xs uppercase tracking-wider">
              <tr>
                <th className="text-left px-4 py-3 font-semibold">Email</th>
                <th className="text-left px-4 py-3 font-semibold">Name</th>
                <th className="text-left px-4 py-3 font-semibold">Subscribed</th>
                <th className="text-left px-4 py-3 font-semibold">Status</th>
                <th className="px-4 py-3"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {filtered.map((s) => (
                <tr key={s.id} className="hover:bg-slate-50/50">
                  <td className="px-4 py-3 text-slate-700 font-medium">{s.email}</td>
                  <td className="px-4 py-3 text-slate-600">{s.name || '—'}</td>
                  <td className="px-4 py-3 text-slate-600">{s.subscribed_at ? new Date(s.subscribed_at).toLocaleDateString() : '—'}</td>
                  <td className="px-4 py-3">
                    <span className={`text-[10px] font-semibold uppercase tracking-wider px-2 py-0.5 rounded-full ${
                      s.status === 'active' ? 'bg-emerald-100 text-emerald-700' : 'bg-slate-100 text-slate-600'
                    }`}>{s.status}</span>
                  </td>
                  <td className="px-4 py-3 text-right">
                    <button onClick={() => remove(s.id)} className="text-slate-400 hover:text-rose-600" title="Delete"><Trash2 size={16} /></button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}
